import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'

interface ManageChoiceResultModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  subtitle?: string
  choices: string[]
  currentValue?: string | null
  resultLabel: string
  onSave: (selected: string) => Promise<void>
}

export function ManageChoiceResultModal({
  open,
  onOpenChange,
  title,
  subtitle,
  choices,
  currentValue,
  resultLabel,
  onSave,
}: ManageChoiceResultModalProps) {
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState('')

  useEffect(() => {
    if (open) {
      setSelected(currentValue ?? '')
    }
  }, [open, currentValue])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selected) {
      toast.error(`Select ${resultLabel.toLowerCase()}`)
      return
    }
    setLoading(true)
    try {
      await onSave(selected)
      onOpenChange(false)
      toast.success(`${resultLabel} saved — points recalculated`)
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Update failed')
    } finally {
      setLoading(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => onOpenChange(false)}>
      <div
        className="w-full max-w-md glass-strong rounded-2xl p-5 sm:p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-display font-bold text-foreground">{title}</h2>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground"
          >
            ×
          </button>
        </div>
        {subtitle && <p className="text-sm text-muted-foreground mb-4">{subtitle}</p>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="block text-sm font-medium text-foreground">
            {resultLabel}
            <select
              className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              required
            >
              <option value="">— Select —</option>
              {choices.map((choice) => (
                <option key={choice} value={choice}>
                  {choice}
                </option>
              ))}
            </select>
          </label>
          {choices.length === 0 && (
            <p className="text-sm text-amber-600">This event has no choices configured.</p>
          )}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              className="px-4 py-2 rounded-lg border border-border hover:bg-muted/50"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
              disabled={loading || !selected}
            >
              {loading ? 'Saving...' : 'Save result'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
